import React from 'react';
import { useIntl } from 'react-intl';
import core from '@gin-betting/core';
import cn from 'classnames';

import { DEFAULT_STREAM_URL, ExtendedRouts } from 'app-constants';
import { BackButton, Icon, LiveIcon } from 'components';
import globalMsg from 'messages';
import { Action, Category } from 'services/analytics';
import { ViewerMode } from '../../constants';
import { StreamHeaderProps } from '../../types';

import aftermatch from 'assets/svg/aftermatch.svg';
import closePinnedVideo from 'assets/svg/close-video.svg';
import compress from 'assets/svg/compress.svg';
import expand from 'assets/svg/expand.svg';
import field from 'assets/svg/football-field.svg';
import playIcon from 'assets/svg/play-icon.svg';
import './streamHeader.scss';

const { isInnerPage, checkOnRoute } = core.utils;

const StreamHeader: React.FC<StreamHeaderProps> = ({
    betradarId,
    disableTheaterMode,
    goToAftermatch,
    hasMatchLog,
    isFullscreenActive,
    isMatchTrackerDisabled,
    isPinned,
    isValidSportId,
    onChangeViewerModeToMatchTracker,
    onChangeViewerModeToStream,
    onStreamUnpin,
    onToggleStreamFullscreen,
    title,
    url,
    viewerMode,
}) => {
    const isInner = isInnerPage();

    const { formatMessage } = useIntl();

    const isStreamMode = viewerMode === ViewerMode.STREAM;
    const isMatchTrackerMode = viewerMode === ViewerMode.MATCH_TRACKER;
    const isNoneMode = viewerMode === ViewerMode.NONE;

    const areButtonsControlsVisible =
        !isPinned && isValidSportId && !isMatchTrackerDisabled;
    const isValidUrl = url && url !== DEFAULT_STREAM_URL;
    const onResultMatchRoute = checkOnRoute(ExtendedRouts.RESULT_MATCH);

    const isFullscreenBtnHidden =
        isNoneMode ||
        disableTheaterMode ||
        (isMatchTrackerMode && isMatchTrackerDisabled);

    return (
        <div
            styleName={cn('container', {
                active: isFullscreenActive,
            })}
            className="__app-StreamHeader-container"
        >
            {!isFullscreenActive && isInner && <BackButton />}

            <LiveIcon />

            <div styleName="title" className="__app-StreamHeader-title">
                {title}
            </div>

            {areButtonsControlsVisible && (
                <div
                    title={formatMessage(globalMsg.stream)}
                    styleName={cn('icon-wrapper ', {
                        'first-icon': true,
                        'is-active': isStreamMode,
                    })}
                    onClick={
                        isValidUrl ? onChangeViewerModeToStream : undefined
                    }
                >
                    <Icon
                        glyph={playIcon}
                        styleName={cn('play-icon', {
                            'is-active': isStreamMode,
                            'is-disabled ': !isValidUrl,
                        })}
                    />
                </div>
            )}

            {areButtonsControlsVisible && (
                <div
                    title={formatMessage(globalMsg.matchTrackerTitle)}
                    styleName={cn('icon-wrapper', {
                        'is-active': isMatchTrackerMode,
                    })}
                    onClick={onChangeViewerModeToMatchTracker}
                >
                    <Icon
                        glyph={field}
                        styleName={cn('field-icon', {
                            'is-active': isMatchTrackerMode,
                            'is-disabled ': !betradarId,
                        })}
                    />
                </div>
            )}

            {!isFullscreenBtnHidden && (
                <div
                    className="__app-StreamHeader-fullscreen"
                    styleName={cn('icon-wrapper', {
                        'has-margin': !areButtonsControlsVisible,
                    })}
                >
                    <div
                        styleName={cn('toggle-icon', {
                            'is-full': isFullscreenActive,
                        })}
                        title={formatMessage(
                            isFullscreenActive
                                ? globalMsg.normalMode
                                : globalMsg.theaterMode
                        )}
                        data-category={Category.LineSportEvent}
                        data-action={Action.OpenFullscreen}
                        onClick={onToggleStreamFullscreen}
                    >
                        <Icon glyph={isFullscreenActive ? compress : expand} />
                    </div>
                </div>
            )}

            {isPinned && !isFullscreenActive && (
                <div
                    styleName={cn('unpin-icon', {
                        'has-margin': disableTheaterMode,
                    })}
                    className="__app-StreamHeader-unpin-icon"
                    onClick={onStreamUnpin}
                    title={`${formatMessage(globalMsg.unpin)} ${formatMessage(
                        globalMsg.stream
                    )}`}
                >
                    <Icon glyph={closePinnedVideo} />
                </div>
            )}

            {onResultMatchRoute && (
                <div
                    styleName="icon-wrapper"
                    // TODO: move to messages.js
                    title={
                        hasMatchLog
                            ? 'Match log'
                            : 'Match log is currently unavailable'
                    }
                >
                    <button
                        onClick={goToAftermatch}
                        className="__app-StreamHeader-aftermatch"
                        styleName={cn('aftermatch-button ', {
                            disabled: !hasMatchLog,
                        })}
                    >
                        <Icon glyph={aftermatch} styleName="aftermatch-icon " />
                    </button>
                </div>
            )}
        </div>
    );
};

export default StreamHeader;
